import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { biometricsSupported, loginWithBiometrics } from "@/lib/webauthn";
import { Loader2, Fingerprint } from "lucide-react";
import { Logo, Wordmark } from "@/components/Logo";
import { toast } from "sonner";

export default function Login() {
  const { login, applyAuth } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [bioLoading, setBioLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const biometricLogin = async () => {
    if (!email.trim()) { setError("Enter your email first, then use biometrics."); return; }
    setBioLoading(true);
    setError("");
    try {
      const data = await loginWithBiometrics(email.trim());
      applyAuth(data);
      toast.success("Welcome back");
      navigate("/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Biometric sign-in failed.");
    } finally {
      setBioLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:block relative grain">
        <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('https://images.pexels.com/photos/159832/justice-law-case-hearing-159832.jpeg?auto=compress&cs=tinysrgb&w=1400')" }} />
        <div className="absolute inset-0 bg-background/80" />
        <div className="relative h-full flex flex-col justify-between p-12">
          <Link to="/" className="flex items-center gap-2.5"><Logo className="w-7 h-7" /><Wordmark className="font-serif text-xl" /></Link>
          <blockquote className="font-serif text-3xl leading-snug max-w-md">"Educate yourself or hire an attorney… your choice."</blockquote>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <h1 className="font-serif text-4xl mb-2">Sign in</h1>
          <p className="text-sm text-muted-foreground mb-8">Continue your study of the archive.</p>

          {error && <div className="mb-4 text-sm text-destructive-foreground bg-destructive/30 border border-destructive px-3 py-2" data-testid="login-error">{error}</div>}

          <label className="block text-xs uppercase tracking-widest font-mono text-muted-foreground mb-2">Email</label>
          <input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            className="w-full bg-muted border border-input px-3 py-2.5 mb-5 rounded-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors" />

          <label className="block text-xs uppercase tracking-widest font-mono text-muted-foreground mb-2">Password</label>
          <input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            className="w-full bg-muted border border-input px-3 py-2.5 mb-2 rounded-sm outline-none focus:border-primary focus:ring-1 focus:ring-primary transition-colors" />

          <div className="mb-6 text-right">
            <Link to="/forgot-password" data-testid="login-forgot-link" className="text-xs text-muted-foreground hover:text-primary transition-colors">Forgot password?</Link>
          </div>

          <button data-testid="login-submit" disabled={loading}
            className="w-full bg-primary text-primary-foreground py-3 rounded-sm text-sm flex items-center justify-center gap-2 hover:-translate-y-[1px] transition-transform disabled:opacity-60">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />} Sign in
          </button>

          {biometricsSupported() && (
            <>
              <div className="flex items-center gap-3 my-5">
                <div className="h-px bg-border flex-1" />
                <span className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground">or</span>
                <div className="h-px bg-border flex-1" />
              </div>
              <button type="button" onClick={biometricLogin} disabled={bioLoading} data-testid="login-biometric"
                className="w-full border border-primary/40 text-primary py-3 rounded-sm text-sm flex items-center justify-center gap-2 hover:bg-primary/10 transition-colors disabled:opacity-60">
                {bioLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Fingerprint className="w-4 h-4" />} Sign in with biometrics
              </button>
            </>
          )}

          <p className="mt-6 text-sm text-muted-foreground">No account? <Link to="/register" className="text-primary hover:underline">Enroll now</Link></p>
        </form>
      </div>
    </div>
  );
}
